import MoneyTextInput from '../MoneyTextInput';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import api from '../../services/api';
import i18n from '../../i18n';
import { keyFor, clearKey, serverMessage } from '../../lib/idempotencyKey.mjs';
import { AccountPick } from '../FormPickers';
import { pickableAccounts, scopedPartnerOptions, resolveSupplierPayable } from '../../lib/resolveSupplierAccount';
import { formatMoney, formatDate } from '../../utils/format';
import { COST_CLASS_LABELS, SCOPE_LABELS } from '../../config/constants';
// Shared door context + done-order rule (Add Discount / Add Fees / Make Payment): salesDoorContext.mjs.
import OpenOrderPicker, { LockedChip } from './OpenOrderPicker';
import useOrderPickerLabel from './useOrderPickerLabel';
import { DrawerActions } from '../../shell/DrawerBody';
import { resolveContext, isOfferable, FEES_OPEN_ORDERS_ONLY } from './salesDoorContext.mjs';
// 41179 (GES-279) — the FE admin signal for the Cancel-fee guard below. `isAdmin` here is /api/me's
// role-derived flag (server resolveAccess → membership of the single is_system 'Admin' role), NOT the
// deprecated user_profiles.is_admin mirror. Same signal Navbar/Settings/PeriodLock already gate on.
import { useAuth } from '../../context/AuthContext';
import { useCreditGate } from './CreditLimitNotice';
import { emoText } from '../../shell/EmojiIcons';
import './SaleFeesPanel.css';
import EnumSelect from '../EnumSelect';
import FeePreview from './FeePreview';
import { offered, scalingFor, modelFields, previewReady, collectShown, lateShown, allocShown, optionFields } from './feeModel.mjs';   // 46987: the sale fee model
import { AmountInput, TextInput } from '../inputs/Input';
import FeeDispositionDrawer from '../../pages/Sales/closeout/FeeDispositionDrawer';   // E6 (47019): a paid fee asks what to do with the money
import ReferenceField from '../inputs/ReferenceField';
  // GES-15: a fee charged to the customer is credit door #10

// ── 873 — SALE FEES INLINE PANEL. The sell-side mirror of Procurement ▸ Landed Costs. ────────────
//
// WHY ONE FILE WHERE LC IS TWO. LC splits into ManageLandedCosts (order picker + existing list) and
// LandedCostEntryPanel (the add form) because the FORM is mounted by four different hosts — the
// Procurement page, DataEntryPage, OrderTabs and GitTab — so it has to stand alone. The fee form has
// exactly ONE host. Splitting it here would mirror an accident of LC's reuse rather than its
// interaction, and would buy an extra props contract for nothing. If a second host ever needs the form
// (the Data Entry mirror is already named as a later build), THAT is the moment to split it.
//
// STYLE: the "v6.2 inline-action-panel" language is a CONVENTION, not a module — there is no shared
// object or class by that name; INP/LABEL/FieldWrap are copied per panel (LandedCostEntryPanel:11-28,
// PurchaseEntryPanel:18-25, SaleEntryPanel:86-110, the last commented "procurement's tokens, copied").
// So they are copied here too, per the convention, with the SELL-SIDE green SEG rather than LC's blue
// toggleBtnStyle.
//
// FORMATTERS: shared format.js, deliberately NOT LC's local `fmt` on hardcoded 'fr-FR' with a hardcoded
// ' F' suffix and raw dates. Mirroring LC's wiring does not mean inheriting its formatting debt.

const INP = {
  border: '1px solid var(--border-2)', borderRadius: 6, padding: '6px 10px',
  fontSize: 13, color: 'var(--text)', background: 'var(--surface)', outline: 'none', width: '100%', boxSizing: 'border-box',
};
// One size for every segmented button in the panel, and whiteSpace:'nowrap' so a long label widens
// nothing and wraps nowhere. `flex: 1` is gone deliberately — it is what made each pair take the size
// of whatever slot it happened to sit in, so no two pairs matched.
// The muted-notice token — the same one LC uses for its selector gate, its empty state and its footer
// line, so the three read as one family.



// ── 874b — SIZING. Rewritten after the first attempt made the form worse, not better. ──────────
//
// WHAT WENT WRONG THE FIRST TIME, so it is not repeated: 873 sized fields with flex ratios, which meant
// a control appearing or vanishing re-divided the row and moved everything. 874's first pass fixed that
// with fixed widths + <Spacer> placeholders — and introduced two new faults. The widths were derived
// from a tidy-looking table rather than from the LABELS, so "Charge customer", "By Quantity" and
// "Proportional" wrapped to two lines and every segmented pair came out a different size. And reserving
// a slot for a control at the END of a row left a visible blank where the control used to be.
//
// THE RULE NOW, and it removes the need for Spacers entirely: an OPTIONAL CONTROL IS ALWAYS LAST IN ITS
// ROW. Then hiding it shortens the row's tail instead of punching a hole in its middle, and nothing to
// its left can move — the stability 874 wanted, without the blank. All Spacers are gone.
//
// BUTTONS ARE ONE SIZE EVERYWHERE. Every segmented button gets the SAME fixed width, wide enough for
// the longest label in the form ("Charge customer"), and never wraps. A pair is therefore exactly
// 2×BTN_W + the gap, so Scaling, Allocation Method, Who-bears-it and Payment Method are all identical
// blocks rather than four different sizes.
// alignItems 'flex-start': a column carrying a description is taller than one that does not, and
// bottom-alignment pushed the short columns' labels and inputs DOWN to meet the tall one's baseline.
// FIX 3 — the bearer caption's own line. It changes length with the treatment, so inside a field it
// shoved its neighbours; on its own full-width line it can say anything without moving a control. The
// minHeight reserves the line even when there is no caption yet, so choosing a type does not nudge the
// rows below it either.



const invalid = (bad) => ({ ...INP, borderColor: bad ? 'var(--crit-bg)' : 'var(--border-2)' });   // AccountPick takes a style object (btnStyle)
const todayStr = () => new Date().toISOString().slice(0, 10);

function FieldWrap({ label, children, className = '', hint, hintRight, title }) {
  // No default flex/minWidth: every caller passes a slot() so the width is the row's decision, not the
  // content's. A field that sizes itself is a field that resizes when a sibling appears.
  //
  // hintRight puts the description BESIDE the input rather than under it — for the last field in a row,
  // where the space to the right is empty anyway and stacking the text would only make that column
  // taller than its neighbours for no reason.
  const body = (
    <>
      <span className="sfp-1">{emoText(label)}</span>
      {children}
      {hint ? <div className="sfp-2">{hint}</div> : null}
    </>
  );
  if (!hintRight) return <div title={title} className={`sfp-field ${className}`}>{body}</div>;
  return (
    <div className="sfp-3">
      <div title={title} className={`sfp-field ${className}`}>{body}</div>
      <div className="sfp-4">{hintRight}</div>
    </div>
  );
}
function Stat({ label, value, tone }) {
  return (
    <div className="sfp-5">
      <div className="sfp-6">{emoText(label)}</div>
      <div className={`sfp-stat${tone ? ' ' + tone : ''}`}>{value}</div>
    </div>
  );
}

// THE FOOTER LINE — the one place the fee panel is MORE dynamic than its template. LC's equivalent is a
// constant string; the fee's must say where THIS fee lands, and that depends on the treatment the chosen
// type carries (§3.4: treatment is a property of the TYPE, stamped at creation, never picked raw).
// Both sentences are corroborated by 872's MEASURED numbers, not only by the design doc:
// contra × charge_customer measured 0 / 0 / 0 (absorbed, exactly) and recovery × charge_customer
// measured revenue +1,000 alongside its expense (gross, exactly).

// The footer says where THIS fee lands, before it is posted. Keyed on INTENT, because that is what the
// user chose; the treatment is derived. Every sentence below is corroborated by 875's measured
// write-test, not only by the design doc: absorb net -1,000 per 1,000 · charge net +the full amount (it
// is pure revenue since 876) · pass-through 0/0/0 by cancellation · absorb-deduct net -1,000 with the
// receivable reduced rather than billed.
const FOOTER_KEYS = { absorb: 'feesPanel.foot.absorb', charge: 'feesPanel.foot.charge', pass_through: 'feesPanel.foot.passThrough', absorb_deduct: 'feesPanel.foot.absorbDeduct' };

export default function SaleFeesPanel({ onCancel, onPosted, initialOpId = '', lockOrder = false, partnerId = '', partnerName = '', feeId = '', disposition = '' }) {
  const { t } = useTranslation('sales');
  const { t: tc } = useTranslation('common');
  const pickerLabel = useOrderPickerLabel();
  const { isAdmin } = useAuth();
  const credit = useCreditGate();
  const [orders, setOrders] = useState([]);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  // When opened from inside an individual order (lockOrder), the sale is pinned to that order and the
  // picker renders read-only — the fee cannot be attributed to a different sale than the one in context.
  const [opId, setOpId] = useState(initialOpId);
  const orderLocked = lockOrder && !!opId;
  const [feeTypes, setFeeTypes] = useState([]);
  const [partners, setPartners] = useState([]);
  const [accounts, setAccounts] = useState([]);

  // rows === null → loading · [] → empty · [...] → rows. LC's three-state discipline, kept.
  const [fees, setFees] = useState(null);
  const [feesErr, setFeesErr] = useState(null);
  // 875 — the parent order's Cash/Credit nature decides the CUSTOMER side. The orders LIST does not
  // carry it (checked: no payment_method on those rows), so it comes from the single-order read, whose
  // lines do: getSalesOrder(op).lines[0].payment_method.
  const [orderNature, setOrderNature] = useState(null);
  const [hasDeliveries, setHasDeliveries] = useState(false);   // the order already has a delivery: the late-fee mode field appears
  const [lateMode, setLateMode] = useState('catch_up');   // catch_up | remainder
  const [collect, setCollect] = useState('now');           // now | per_delivery
  const [allocation, setAllocation] = useState('by_value'); // by_value | by_quantity

  // 875 — INTENT replaces the fee-type dropdown AND the bearer control. The user states what they mean;
  // treatment, bearer and fee_type_id are all derived from it (see INTENT below), because none of the
  // three can be inferred from the other fields and asking for them separately made the user assemble
  // the meaning themselves.
  const [intent, setIntent] = useState('absorb');
  const [amount, setAmount] = useState('');
  const [customerAccountId, setCustomerAccountId] = useState('');
  // 878 — ATTRIBUTION, mirroring a normal expense/revenue line: the user picks a BUCKET and the leg
  // inherits that bucket's default cost_class / scope, overridable. No fee-specific defaulting.
  const [subtypes, setSubtypes] = useState([]);
  const [entities, setEntities] = useState({ bands: [], farms: [], locations: [] });
  const [subtypeId, setSubtypeId] = useState('');
  const [costClass, setCostClass] = useState('');
  const [scope, setScope] = useState('');
  const [entityId, setEntityId] = useState('');
  const [scaling, setScaling] = useState('proportional');
  const [scalingTouched, setScalingTouched] = useState(false);
  const [vendorId, setVendorId] = useState('');
  const [payMethod, setPayMethod] = useState('Cash');
  const [accountFrom, setAccountFrom] = useState('');
  const [paymentDays, setPaymentDays] = useState('');
  const [receivableDays, setReceivableDays] = useState('');
  const [date, setDate] = useState(todayStr());
  const [notes, setNotes] = useState(''); const [reference, setReference] = useState('');   // W13 (E9): the fee's OWN optional reference (set at creation only)
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const preselDone = useRef(false);   // pre-select once, not on every reload after a post
  const [cancelFor, setCancelFor] = useState(null);
  const [cancelDisp, setCancelDisp] = useState('written_off');
  // 42013a (GES-284 ruling 4) — the cash/bank account a `refunded_to_bank` disposition is PAID FROM.
  // Required by the server for that disposition and ignored for the other two, so the control appears
  // only when it is needed. No default is guessed: a refund moves real money out of a real account.
  const [cancelRefundAcct, setCancelRefundAcct] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelErr, setCancelErr] = useState(null);
  const [feeDisp, setFeeDisp] = useState(null);   // { fee_id, plan } the write-off form for a PAID fee
  const cancelKey = useRef(null);   // GES-296 Q2: one idempotency key per cancel ACTION (reused by every submit of it)
  const createKey = useRef(null);   // 46987: one idempotency key per create action (a fixed charge is recognised at once: the server requires it)
  const [pv, setPv] = useState({ data: null, error: null, loading: false });

  // ── masters, once ──────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // fb-196: opened from a customer page -> ONLY that customer's orders (server-side filter, also far fewer rows to load)
    // Owner rule: the picker asks only for OPEN (not done) orders. A locked in-order door keeps the old unfiltered read so
    // its chip/customer still resolve when the locked order is done (no regression).
    const listParams = (orderLocked || !FEES_OPEN_ORDERS_ONLY) ? { page_size: 200, partner_id: partnerId }
      : resolveContext({ partnerId }).listParams;
    api.getSalesOrders(listParams).then(d => {
      const seen = new Map();
      for (const r of (d?.rows || [])) if (!seen.has(r.operation_id)) seen.set(r.operation_id, r);
      setOrders([...seen.values()].sort((a, b) => String(b.op_date || '').localeCompare(String(a.op_date || ''))));
    }).catch(() => setOrders([])).finally(() => setOrdersLoaded(true));
    // The picker's contents are the ENDPOINT's decision, not this component's: /fee-types returns only
    // is_active rows (db/491), so pass_through never arrives here. Adding a treatment filter in this
    // file would put the enabled set in two places and guarantee they drift.
    api.getFeeTypes().then(t => setFeeTypes(t || [])).catch(() => setFeeTypes([]));
    api.getPartners().then(ps => setPartners((ps || []).filter(p => p.type === 'Supplier'))).catch(() => setPartners([]));
    api.getAccounts().then(a => setAccounts(a || [])).catch(() => setAccounts([]));
    api.request('/finance-subtypes').then(t => setSubtypes(t || [])).catch(() => setSubtypes([]));
    // THE ENTRY-FORM PROTOCOL, not the re-allocation one. These three loaders are copied from the
    // Expense form (DataEntryPage.jsx:2359-2361) verbatim, because a fee IS an expense/revenue entry
    // and must offer the same targets a new expense would.
    //
    // WHAT I HAD WRONG: I had lifted the RE-ALLOCATION page's loaders — `/bands` raw,
    // `/farms?include_inactive=true`, `/locations?all=true`. Those deliberately INCLUDE dormant
    // entities, and correctly so: that page renders historical rows and a decommissioned farm's name
    // still has to resolve. An ENTRY form is the opposite case — offering a done band as a target for
    // new money is how you attribute today's cost to a band that closed months ago.
    //
    // getProcurementBands() is the shared "Purchase / Expense / Finance" band rule (api.js:257):
    // dormant filtered out, band_status active OR queued, and deliberately NOT birds-gated — a queued
    // band is a legal target for a cost (579), which is why this is not getBioOpBands().
    Promise.all([
      api.getProcurementBands().catch(() => []),
      api.getFarmsLite().then(fs => (fs || []).filter(f => !f.is_inactive)).catch(() => []),
      api.getLocations().then(ls => (ls || []).filter(l => !l.is_archived)).catch(() => []),
    ]).then(([bands, farms, locations]) => setEntities({ bands: bands || [], farms: farms || [], locations: locations || [] }));
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps -- masters once per mount (partnerId/lock are mount-time props)

  // ── the sale's existing fees ───────────────────────────────────────────────────────────────────
  const loadFees = useCallback(() => {
    if (!opId) { setFees(null); setOrderNature(null); setHasDeliveries(false); return; }
    setFeesErr(null);
    api.getSalesFees(opId)
      .then(d => {
        const list = (d?.sales_fees || []).filter(f => f.stamped_treatment !== 'discount');
        setFees(list);
        // row menu on the order page: open pre-selected on that fee (+ the chosen disposition)
        if (feeId && !preselDone.current && list.some(f => f.id === feeId)) { preselDone.current = true; setCancelFor(feeId); if (disposition) setCancelDisp(disposition); }
      })   // GES-16: discounts live in the Discount panel
      .catch(() => { setFees([]); setFeesErr(t('feesPanel.err.loadFees')); });
    api.getSalesOrder(opId)
      .then(d => { setOrderNature((d?.lines || [])[0]?.payment_method || null); setHasDeliveries((d?.lines || []).some(l => Number(l.qty_delivered) > 0 || l.delivery_status === 'delivered')); })
      .catch(() => { setOrderNature(null); setHasDeliveries(false); });
  }, [opId, t, feeId, disposition]);
  useEffect(() => { loadFees(); }, [loadFees]);

  const order = useMemo(() => orders.find(o => o.operation_id === opId) || null, [orders, opId]);

  // Open first, then Closed — `order_lifecycle` is the same field the Orders list groups on, so the
  // two surfaces agree about what "open" means rather than each deciding for itself. The label keeps
  // every field that made the old <select> searchable by eye, because Combobox filters on the label.
  const saleOptions = useMemo(() => {
    const mk = o => ({
      value: o.operation_id,
      label: pickerLabel(o),
      group: String(o.order_lifecycle || '').toLowerCase() === 'closed' ? 'Closed' : 'Open',
    });
    const offered = FEES_OPEN_ORDERS_ONLY ? orders.filter(isOfferable) : orders;   // done orders: order page only
    const open = offered.filter(o => String(o.order_lifecycle || '').toLowerCase() !== 'closed').map(mk);
    const closed = offered.filter(o => String(o.order_lifecycle || '').toLowerCase() === 'closed').map(mk);
    return [...open, ...closed];
  }, [orders, pickerLabel]);

  // ── 875 — THE INTENT TABLE. One user-facing choice; everything the poster needs falls out of it. ──
  // The three seeded fee_types map 1:1 onto the three treatments, so fee_type_id is RESOLVED here
  // rather than asked for — sales_fees.fee_type_id is NOT NULL and the poster hard-requires it, and a
  // dropdown that only ever has one right answer per intent is a question with no information in it.
  // pass_through maps to its OWN seeded type (not to contra's) so stamped_treatment lands as
  // 'pass_through' with no extra field — that stamp is what keeps "this was a pass-through" on the row
  // and the equal-lock re-checkable.
  const INTENTS = {
    absorb:        { treatment: 'contra_expense',   bearer: 'absorb',          deduct: false },
    absorb_deduct: { treatment: 'contra_expense',   bearer: 'absorb',          deduct: true  },
    charge:        { treatment: 'recovery_revenue', bearer: 'charge_customer', deduct: false },
    pass_through:  { treatment: 'pass_through',     bearer: 'charge_customer', deduct: false },
  };
  const treatment = INTENTS[intent].treatment;
  const bearer = INTENTS[intent].bearer;
  // ABSORB-DEDUCT shares absorb's treatment and bearer — what differs is the LEG PAIRING, and only this
  // flag tells the poster which: credit the receivable (or the cash it came out of) rather than a vendor
  // payable. It is the one thing about the intent that cannot be inferred from the other two.
  const isDeduct = INTENTS[intent].deduct;

  // ── 878 — THE SIDE A BUCKET MUST BE ON. absorb / deduct / pass-through post Expense legs; a charge
  // posts one Other Revenue leg. The server refuses a mismatch; this filter means the user never sees
  // one to pick. Same `applies_to` column the expense grid filters on — not a fee-only vocabulary.
  const bucketSide = intent === 'charge' ? 'Other Revenue' : 'Expense';
  const bucketOptions = subtypes.filter(b => !b.engine_only && (b.applies_to || 'Expense') === bucketSide);
  const bucket = subtypes.find(b => b.id === subtypeId) || null;
  // A revenue leg has no cost class (860 / 778#4) — the server refuses one, so the control is absent.
  const showCostClass = intent !== 'charge';
  const entityList = scope === 'band' ? entities.bands : scope === 'farm' ? entities.farms : scope === 'location' ? entities.locations : [];

  // MIRRORS DataEntryPage.jsx:2043 handleSubtypeChange, deliberately and verbatim in shape: picking a
  // bucket seeds the class and scope from ITS OWN Settings defaults and CLEARS the entity, so a stale
  // band cannot survive a scope change. Everything stays overridable afterwards — the established
  // pattern is a soft drift advisory, never a lock.
  // Changing intent can flip the required side (Expense <-> Other Revenue), stranding a bucket the
  // server would now refuse. Clear it rather than let the user meet a 422 they did not cause.
  useEffect(() => {
    if (subtypeId && bucket && (bucket.applies_to || 'Expense') !== bucketSide) {
      setSubtypeId(''); setCostClass(''); setScope(''); setEntityId('');
    }
  }, [bucketSide, subtypeId, bucket]);

  function pickBucket(id) {
    const b = subtypes.find(x => x.id === id);
    setSubtypeId(id);
    setCostClass(b?.default_cost_class || '');
    setScope(b?.default_scope || '');
    setEntityId('');
  }
  const feeTypeId = useMemo(
    () => (feeTypes.find(t => t.treatment === treatment) || {}).id || '',
    [feeTypes, treatment]);
  const isPT = intent === 'pass_through';
  const orderIsCash = String(orderNature || '').toLowerCase() === 'cash';
  const orderDelivered = ['delivered', 'recognised'].includes(String(order?.delivery_status || order?.o_delivery_status || '').toLowerCase());
  const deductTiming = orderDelivered
    ? t('feesPanel.timing.now')
    : t('feesPanel.timing.later');

  // The intent's fee type seeds the scaling control, but ONLY until the user touches it — seeding on
  // every change would make a common field lose the user's value when the intent toggles.
  useEffect(() => {
    const t = feeTypes.find(x => x.treatment === treatment);
    if (t && !scalingTouched && t.default_scaling) setScaling(t.default_scaling);
  }, [feeTypes, treatment, scalingTouched]);

  // Vendor → payable account, auto-filled and locked on Credit (LC's resolveSupplierPayable wiring).
  const selVendor = partners.find(p => p.id === vendorId) || null;
  const { accountId: resolvedAcct, locked: acctLocked } = resolveSupplierPayable(selVendor, accounts);
  const creditLocked = payMethod === 'Credit' && acctLocked;
  useEffect(() => {
    if (payMethod !== 'Credit' || !vendorId) return;
    if (resolvedAcct) setAccountFrom(resolvedAcct);
    if (selVendor?.payment_days != null) setPaymentDays(String(selVendor.payment_days));
  }, [payMethod, vendorId, resolvedAcct]);   // eslint-disable-line react-hooks/exhaustive-deps

  const cashBankAccounts = pickableAccounts(accounts, ['cash', 'bank']);
  const supplierAccounts = pickableAccounts(accounts, ['supplier']);
  const selPayAcct = accounts.find(a => a.id === accountFrom) || null;
  const { options: vendorOptions, scope: vendorScope } = scopedPartnerOptions(selPayAcct, partners, partners);

  const charging = bearer === 'charge_customer';
  // 46987: the scaling actually offered for this intent (a not-offered choice falls back to fixed; absorb deduct has none)
  const effScaling = scalingFor(intent, scaling);

  function resetForm() {
    setIntent('absorb'); setAmount(''); setCustomerAccountId(''); setReference('');
    setSubtypeId(''); setCostClass(''); setScope(''); setEntityId('');
    setScaling('proportional'); setScalingTouched(false); setCollect('now'); setLateMode('catch_up'); setAllocation('by_value');
    setVendorId(''); setPayMethod('Cash'); setAccountFrom(''); setPaymentDays('');
    setReceivableDays(''); setDate(todayStr()); setNotes(''); setError(null);
  }

  function validate() {
    if (!opId) { setError(t('feesPanel.err.pickSale')); return false; }
    if (!feeTypeId) { setError(t('feesPanel.err.typesLoading')); return false; }
    if (!(parseFloat(amount) > 0)) { setError(t('feesPanel.err.amountPositive')); return false; }
    if (charging && orderIsCash && !customerAccountId) {
      setError(t('feesPanel.err.cashAccount')); return false;
    }
    // A credit absorb-deduct has a real vendor payable (named vendor or automatic Various); a cash
    // deduct keeps its existing cash-account shape. The vendor is optional only because Various is real.
    if (['band', 'farm', 'location'].includes(scope) && !entityId) {
      setError(t('feesPanel.err.scope', { scope: SCOPE_LABELS[scope].toLowerCase() })); return false;
    }
    if (isDeduct && orderIsCash && !accountFrom) {
      setError(t('feesPanel.err.cameOut')); return false;
    }
    // 876 — a CHARGE has no vendor side at all now (it is pure revenue), so nothing to collect here.
    if (!isDeduct && intent !== 'charge') {
      if (!vendorId) { setError(t('feesPanel.err.pickVendor')); return false; }
      if (!accountFrom) { setError(t('feesPanel.err.pickAccount')); return false; }
      if (payMethod === 'Credit' && !paymentDays) { setError(t('feesPanel.err.payDays')); return false; }
    }
    return true;
  }

  // 46987: one body for the create AND the 'What will happen' preview. The model fields (scaling, allocation) come from
  // feeModel.mjs: a fixed fee sends no allocation; a proportional charge sends by_value; absorb deduct sends none (the server decides).
  // Amounts go as the typed exact text (no float), as the server's exact-cents door expects.
  function buildBody() {
    const amt = String(amount || '').trim();
    return {
        operation_id: opId,
        fee_type_id: feeTypeId,
        amount: amt,
        ...modelFields(intent, scaling, allocation, orderIsCash),
        ...optionFields({ intent, scaling, orderIsCash, hasDeliveries, collect, lateMode }),
        bearer,
        // 876 — one amount per fee. Only pass-through still names a customer amount, and only so the
        // SERVER's equal-lock has something to check; a UI-only lock leaks.
        // 876 — only pass-through still names a customer amount, and only so the SERVER's equal-lock has
        // something to check. A charge sends one amount; the poster reads it as the customer charge.
        ...(isPT ? { customer_amount: amt } : {}),
        ...(charging && orderIsCash ? { customer_account_id: customerAccountId } : {}),
        ...(isDeduct ? { reduces_receivable: true } : {}),
        // 878 — the bucket and its inherited attribution, exactly as a normal line sends them.
        ...(subtypeId ? { subtype: subtypeId } : {}),
        ...(showCostClass && costClass ? { cost_class: costClass } : {}),
        ...(scope ? { scope } : {}),
        ...(scope === 'band' && entityId ? { band_id: entityId } : {}),
        ...(scope === 'farm' && entityId ? { farm_id: entityId } : {}),
        ...(scope === 'location' && entityId ? { location_id: entityId } : {}),
        partner_id: intent === 'charge' ? null : (vendorId || null),
        // A credit absorb-deduct optionally names its vendor; without one, the server uses the system
        // Supplier Various and its payable. Cash deducts retain their existing cash-account shape.
        account_from_id: intent === 'charge' ? null : (isDeduct ? (orderIsCash ? accountFrom : null) : accountFrom),
        payment_days: payMethod === 'Credit' ? (parseInt(paymentDays, 10) || null) : null,
        // OWNER RULING (874c): BLANK CUSTOMER TERMS MEANS 0 — DUE IMMEDIATELY, never NULL.
        // §3.3's original "NULL = follow the order" cannot be honoured here: the parent may be a CASH
        // sale, which carries payment_days 0 or NULL, so there is nothing meaningful to follow. And a
        // NULL on this leg is actively harmful — computeAged derives `due = date + payment_days` and
        // yields NULL when terms are absent (:13786), so the receivable would age forever without ever
        // falling due (the 765 lesson, the same one 865's PUT guard refuses in-place edits over).
        // 0 is the honest reading of an unstated term on a charged fee: payable on presentation.
        // Only a CREDIT order's customer leg ages; a cash-order fee is settled at the till.
        receivable_payment_days: charging && !orderIsCash ? (receivableDays === '' ? 0 : (parseInt(receivableDays, 10) || 0)) : null,
        date,
        notes: notes || null,
        ...(reference.trim() ? { reference_id: reference.trim() } : {}),
    };
  }

  async function submit(sent = null) {
    setError(null); credit.clear();
    if (!sent && !validate()) return;
    setSubmitting(true);
    let body = sent;
    try {
      if (!sent) { body = buildBody(); body.idempotency_key = keyFor(createKey, JSON.stringify(body)); }
      await api.createSalesFee(body);
      clearKey(createKey);
      resetForm();
      loadFees();                       // the panel's own list
      if (onPosted) onPosted();         // the Orders list, by key remount (SalesPage:52)
    } catch (e) {
      if (credit.catchCredit(e.body, (ov) => submit({ ...body, ...ov }))) return;   // GES-15: the SAME body + override
      setError(serverMessage(e.body, i18n.language) || tc('requestFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  // 46987: 'What will happen', from the server (POST /api/sales-fees/preview, read-only), debounced while the user types.
  const pvBody = opId && previewReady({ opId, feeTypeId, amount }) ? JSON.stringify(buildBody()) : '';
  useEffect(() => {
    if (!pvBody) { setPv({ data: null, error: null, loading: false }); return undefined; }
    let live = true; setPv(s => ({ ...s, loading: true }));
    const h = setTimeout(async () => {
      try {
        const r = await api.previewSalesFee(JSON.parse(pvBody));
        if (live) setPv(r.ok ? { data: r.data, error: null, loading: false } : { data: null, error: serverMessage(r.data, i18n.language) || tc('requestFailed'), loading: false });
      } catch (e) { if (live) setPv({ data: null, error: serverMessage(e.body, i18n.language) || tc('requestFailed'), loading: false }); }
    }, 400);
    return () => { live = false; clearTimeout(h); };
  }, [pvBody, t, tc]);

  async function doCancel(fee) {
    setCancelErr(null); setCancelBusy(true);
    try {
      // 42013a — the three dispositions now post THREE DIFFERENT ledger outcomes (written_off = a
      // bad debt to Cancellation write-off; released_to_pool = a liability on Customer Credit that
      // shows in the customer's on-account pool; refunded_to_bank = real cash out of bank/cash). The
      // refund account is sent only for the one that needs it; the server 422s if it is missing.
      const refund_account_id = cancelDisp === 'refunded_to_bank' ? (cancelRefundAcct || null) : null;
      const date = todayStr();
      const idempotency_key = keyFor(cancelKey, [fee.id, cancelDisp, refund_account_id, date].join('|'));
      const r = await api.cancelSalesFee(fee.id, { disposition: cancelDisp, date, refund_account_id, idempotency_key });
      if (!r.ok && r.data && ['FEE_DOOR_CLOSED_OUT_REASON_REQUIRED', 'FEE_DOOR_CLOSED_OUT_ADMIN_ONLY'].includes(r.data.code)) { setFeeDisp({ fee_id: fee.id, plan: null }); return; }   // R-V: a closed-out order asks for the reason in the shared drawer (its preview says closed_out)
      if (!r.ok && r.data && r.data.code === 'FEE_DISPOSITION_REQUIRED') { setFeeDisp({ fee_id: r.data.fee_id || fee.id, plan: r.data.plan || null }); return; }
      if (!r.ok) { setCancelErr(serverMessage(r.data, i18n.language) || t('feesPanel.err.cancelStatus', { status: r.status })); return; }
      clearKey(cancelKey);   // a replay (200) lands here too: same success state
      setCancelFor(null);
      loadFees();
      if (onPosted) onPosted();
    } catch (e) {
      setCancelErr(serverMessage(e.body, i18n.language) || tc('requestFailed'));
    } finally { setCancelBusy(false); }
  }

  return (
    <div>
      {feeDisp && <FeeDispositionDrawer mode="writeoff" id={feeDisp.fee_id} op={opId} initial={feeDisp.plan} isAdmin={isAdmin} onClose={() => setFeeDisp(null)}
        onDone={() => { clearKey(cancelKey); setCancelFor(null); loadFees(); if (onPosted) onPosted(); }} />}
      {/* ── STEP 1 — the sale selector. Mirrors LC's order selector, including the rule that nothing
           else RENDERS (not merely "is disabled") until a sale is chosen. ── */}
      {/* A TYPE-AHEAD, GROUPED OPEN-THEN-CLOSED. A plain <select> of every order was unusable once the
          list grew — you cannot search it, and a closed order from months ago sits between two live
          ones. Combobox already supports groups natively (an optional `group` per option, headings
          rendered between them), so this is the existing control used as intended, not a new one.
          GROUP ORDER IS THE ARRAY ORDER — Combobox keeps first-seen group order — so Open is built
          first deliberately: the order you are adding a fee to is almost always a live one. */}
      {partnerId && !orderLocked && <LockedChip text={partnerName || partnerId} />}
      <div className="sfp-7">
        <span className="sfp-1">{t('feesPanel.sale')}{orderLocked ? ` · ${t('feesPanel.locked')}` : ''}</span>
        {orderLocked ? (
          <div className="sfp-8" title={opId}>
            {opId}{order?.customer_name ? ` · ${order.customer_name}` : ''}
          </div>
        ) : (
          <OpenOrderPicker
            value={opId}
            onChange={v => { setOpId(v || ''); resetForm(); clearKey(cancelKey); setCancelFor(null); }}
            placeholder={t('feesPanel.searchPh')}
            options={saleOptions}
            loaded={ordersLoaded}
            onLeave={onCancel}
          />
        )}
      </div>

      {!opId ? (
        <div className="sfp-9">{t('feesPanel.pickHint')}</div>
      ) : (
        <>
          {/* The CUSTOMER is read-only by construction: the server derives it from the sale (:17535),
              so a fee can never bill anyone other than the sale's customer. Showing it makes that
              visible instead of merely true. */}
          <div className="sfp-10">
            <Trans t={t} i18nKey="feesPanel.billsCustomer" values={{ name: order?.customer_name || '—' }} components={{ b: <strong /> }} />
          </div>

          {/* ── STEP 2a — existing fees ── */}
          <div className="sfp-11">{t('feesPanel.existing')}</div>
          {fees === null && <div className="sfp-12">{t('feesPanel.loading')}</div>}
          {feesErr && <div className="sfp-13">{feesErr}</div>}
          {fees !== null && fees.length === 0 && !feesErr && (
            <div className="sfp-14">{t('feesPanel.noneYet')}</div>
          )}
          {(fees || []).map(f => (
            <div key={f.id} className="sfp-15">
              <div className="sfp-16">
                <div>
                  {/* 894d — NAME THE BUCKET, not the fee type. `fee_type_label` is not a category anyone
                      chose: fee_type_id is resolved from the treatment a few lines up (:315-317) purely to
                      satisfy a NOT NULL column left by the superseded fee-type model, so it rendered the
                      SAME name for every fee sharing a treatment — an eggcrate scrap sale and a manure
                      sale both read "Livraison / Distribution". The bucket is what was actually picked.
                      Falls back to the old label only when the fee has no leg to read a bucket off yet. */}
                  <div className="sfp-17">
                    {f.bucket ? String(f.bucket).replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) : '—'}
                    <span className="sfp-18">
                      {' · '}{f.bearer === 'charge_customer' ? t('feesPanel.chargedToCustomer') : t('feesPanel.absorbed')}
                    </span>
                  </div>
                  <div className="sfp-19">
                    {formatDate(f.date)}{f.notes ? ` · ${f.notes}` : ''}
                  </div>
                </div>
                {/* 41179 (GES-279) admin-only action (POST /api/sales-fees/:id/cancel is admin-gated). Wave-1 C4: it stays VISIBLE to a
                    non-admin, disabled, with the reason as its title / aria-label (a hidden action cannot explain itself). */}
                {(
                  <div className="sfp-20">
                    <button type="button" className="btn" disabled={!isAdmin}
                      title={isAdmin ? undefined : t('feesPanel.adminOnly')} aria-label={isAdmin ? undefined : `${t('feesPanel.cancelFee')} — ${t('feesPanel.adminOnly')}`}
                      aria-expanded={cancelFor === f.id} onClick={() => { clearKey(cancelKey); setCancelFor(cancelFor === f.id ? null : f.id); setCancelErr(null); }}>
                      {t('feesPanel.cancelFee')}
                    </button>
                  </div>
                )}
              </div>
              <div className="sfp-22">
                <Stat label={t('feesPanel.amountStat')} value={formatMoney(f.amount)} />
                {/* DERIVED at read by deriveFeeDisposition — never a parallel status computed here.
                    (The old ':17444' citation here was stale; 42013 re-located it. Line numbers drift,
                    so the function is named and not numbered.) */}
                <Stat label={t('feesPanel.writtenOff')} value={formatMoney(f.written_off)} tone={f.written_off > 0 ? 'crit' : undefined} />
                <Stat label={t('feesPanel.released')} value={formatMoney(f.released_to_pool)} tone={f.released_to_pool > 0 ? 'attn' : undefined} />
                <Stat label={t('feesPanel.refunded')} value={formatMoney(f.refunded_to_bank)} tone={f.refunded_to_bank > 0 ? 'warn' : undefined} />
                <Stat label={t('feesPanel.dispositionStat')} value={f.disposition === 'none' ? '—' : (f.disposition || '—')} />
              </div>
              {cancelFor === f.id && (
                <div className="sfp-23">
                  {/* Cancel ONLY — no Adjust, and no inline money edit. 872's guard 422s any money edit
                      on a fee leg, so an Adjust affordance here would be a button that cannot work. */}
                  <div className="sfp-24">
                    <FieldWrap label={t('feesPanel.dispositionStat')} className="sfp-mw-220">
                      <EnumSelect bare value={cancelDisp} onChange={v => setCancelDisp(v)}>
                        <option value="written_off">{t('feesPanel.dispWrittenOff')}</option>
                        <option value="released_to_pool">{t('feesPanel.dispPool')}</option>
                        <option value="refunded_to_bank">{t('feesPanel.dispBank')}</option>
                      </EnumSelect>
                    </FieldWrap>
                    {/* 42013a — REFUND ACCOUNT, rendered ONLY for the disposition that moves cash.
                        `refunded_to_bank` is now a real cash-out (bank|cash -> client, the two cells
                        this build opens), so the money has to come from somewhere nameable. The
                        server refuses the post without it rather than defaulting one, and this
                        control is the same cash/bank picker the fee form above already uses. */}
                    {cancelDisp === 'refunded_to_bank' && (
                      <FieldWrap label={t('feesPanel.refundFrom')} className="sfp-mw-260">
                        <AccountPick accounts={cashBankAccounts} value={cancelRefundAcct} onChange={setCancelRefundAcct} placeholder={t('feesPanel.chooseAcct')} btnStyle={INP} />
                      </FieldWrap>
                    )}
                    <button type="button" className="btn danger"
                      disabled={cancelBusy || (cancelDisp === 'refunded_to_bank' && !cancelRefundAcct)}
                      title={cancelDisp === 'refunded_to_bank' && !cancelRefundAcct ? t('feesPanel.refundFrom') : undefined}
                      onClick={() => doCancel(f)}>
                      {cancelBusy ? t('feesPanel.cancelling') : t('feesPanel.confirmCancel')}
                    </button>
                  </div>
                  {cancelErr && <div className="sfp-26">{cancelErr}</div>}
                </div>
              )}
            </div>
          ))}

          {/* ── STEP 2b — the add form ── */}
          <div className="sfp-27">
            <div className="sfp-11">{t('feesPanel.addTitle')}</div>

            {/* ROW 1 — THE INTENT, then what and when. The intent is FIRST because it decides which of
                the rows below exist at all; asking it last would reshape the form under the user. */}
            <div className="sfp-28">
              <FieldWrap label={t('feesPanel.whatFee')} className="sfp-seg-4"
                title={t('feesPanel.whatFeeTip')}>
                <div className="sfp-seg-group">
                  <button type="button" className={`sfp-seg${(intent === 'absorb') ? ' on' : ''}`} onClick={() => setIntent('absorb')}
                    title={t('feesPanel.i.absorbAddTip')}>
                    {t('feesPanel.i.absorbAdd')}
                  </button>
                  <button type="button" className={`sfp-seg${(intent === 'absorb_deduct') ? ' on' : ''}`} onClick={() => setIntent('absorb_deduct')}
                    title={t('feesPanel.i.absorbDeductTip')}>
                    {t('feesPanel.i.absorbDeduct')}
                  </button>
                  <button type="button" className={`sfp-seg${(intent === 'charge') ? ' on' : ''}`} onClick={() => setIntent('charge')}
                    title={t('feesPanel.i.chargeTip')}>
                    {t('feesPanel.i.charge')}
                  </button>
                  <button type="button" className={`sfp-seg${(isPT) ? ' on' : ''}`} onClick={() => setIntent('pass_through')}
                    title={t('feesPanel.i.passThroughTip')}>
                    {t('feesPanel.i.passThrough')}
                  </button>
                </div>
              </FieldWrap>

              {/* 876 — ONE amount per intent, always. Charge used to add a SECOND input (vendor cost)
                  whose gap from the charge was the margin — but one `amount` column cannot store a
                  split, so an edit could never reconstruct it (500/1500 -> 1000). A marked-up cost is
                  now two fees: an Absorb for the cost, a Charge for the revenue, each editable. */}
              <FieldWrap label={intent === 'charge' ? t('feesPanel.customerPays') : t('feesPanel.amountReq')} className="sfp-w-amount">
                <MoneyTextInput value={amount} onChange={e => setAmount(e.target.value)}
                  placeholder="0" className={`sfp-inp${(!!amount && !(parseFloat(amount) > 0)) ? ' bad' : ''}`} />
              </FieldWrap>

              <FieldWrap label={t('feesPanel.dateReq')} className="sfp-w-date">
                <TextInput type="date" value={date} onChange={e => setDate(e.target.value)} className="sfp-25" />
              </FieldWrap>
            </div>

            {/* ROW 2 — how it splits. Allocation Method is last, so hiding it on Fixed ends the row. */}
            <div className="sfp-28">
              {/* 46987 (owner model): Fixed = earned on the fee date; Proportional = earned with each delivery. Options the
                  treatment does not offer are DISABLED with the reason; absorb deduct has no choice (a fixed line). The server
                  enforces the same rules. No clock control: the scaling decides it. */}
              <FieldWrap label={t('feesPanel.scaling')} className="sfp-seg-2">
                {isDeduct ? (
                  <div className="sfp-fixedline" data-testid="fee-deduct-line">{t('feesPanel.model.deductLine')}</div>
                ) : (<>
                  <div className="sfp-seg-group">
                    {['proportional', 'fixed'].map(s => (
                      <button key={s} type="button" data-testid={`fee-scaling-${s}`} disabled={!offered(intent, s)}
                        title={offered(intent, s) ? t(s === 'fixed' ? 'feesPanel.model.fixedHint' : 'feesPanel.model.propHint') : t('feesPanel.model.notOffered', { treatment: t(`feesPanel.model.treatment.${intent}`) })}
                        className={`sfp-seg${(effScaling === s) ? ' on' : ''}`}
                        onClick={() => { setScaling(s); setScalingTouched(true); }}>{t(`feesPanel.${s}`)}</button>
                    ))}
                  </div>
                  <div className="sfp-model-hint">
                    <div>{t('feesPanel.model.fixedHint')}</div>
                    <div data-testid="fee-prop-hint" className={offered(intent, 'proportional') ? '' : 'off'}>
                      {offered(intent, 'proportional') ? t('feesPanel.model.propHint') : t('feesPanel.model.notOffered', { treatment: t(`feesPanel.model.treatment.${intent}`) })}
                    </div>
                  </div>
                </>)}
              </FieldWrap>
              {allocShown({ intent, scaling: effScaling, orderIsCash }) && (
                <FieldWrap label={t('feesPanel.allocMethod')} className="sfp-seg-2" hint={t('feesPanel.model.allocHint')}>
                  <div className="sfp-seg-group">
                    {[['by_value', 'byValue', 'fee-alloc-byvalue'], ['by_quantity', 'byQty', 'fee-alloc-byqty']].map(([v, k, id]) => (
                      <button key={v} type="button" data-testid={id} aria-pressed={allocation === v} className={`sfp-seg${allocation === v ? ' on' : ''}`}
                        onClick={() => setAllocation(v)}>{t(`feesPanel.${k}`)}</button>
                    ))}
                  </div>
                </FieldWrap>
              )}
            </div>

            {/* GES-438 Collect + GES-439 late-fee mode (live since 963). The server enforces where each applies; the form shows the same. */}
            {(collectShown({ intent, scaling: effScaling, orderIsCash }) || lateShown({ intent, scaling: effScaling, orderIsCash, hasDeliveries })) && <div className="sfp-28">
              {collectShown({ intent, scaling: effScaling, orderIsCash }) && (
                <FieldWrap label={t('feesPanel.collect.label')} className="sfp-seg-2" hint={t('feesPanel.collect.hint')}>
                  <div className="sfp-seg-group">
                    {[['now', 'now', 'fee-collect-now'], ['per_delivery', 'perDelivery', 'fee-collect-per-delivery']].map(([v, k, id]) => (
                      <button key={v} type="button" data-testid={id} aria-pressed={collect === v} className={`sfp-seg${collect === v ? ' on' : ''}`}
                        onClick={() => setCollect(v)}>{t(`feesPanel.collect.${k}`)}</button>
                    ))}
                  </div>
                  <div className="sfp-model-hint" data-testid="fee-collect-note">{t(collect === 'per_delivery' ? 'feesPanel.collect.perDeliveryHint' : 'feesPanel.collect.nowHint')}</div>
                </FieldWrap>)}
              {lateShown({ intent, scaling: effScaling, orderIsCash, hasDeliveries }) && (
                <FieldWrap label={t('feesPanel.late.label')} className="sfp-seg-2" hint={t('feesPanel.late.hint')}>
                  <div className="sfp-seg-group">
                    {[['catch_up', 'catchUp', 'fee-late-catchup'], ['remainder', 'remainder', 'fee-late-remainder']].map(([v, k, id]) => (
                      <button key={v} type="button" data-testid={id} aria-pressed={lateMode === v} className={`sfp-seg${lateMode === v ? ' on' : ''}`}
                        onClick={() => setLateMode(v)}>{t(`feesPanel.late.${k}`)}</button>
                    ))}
                  </div>
                  <div className="sfp-model-hint" data-testid="fee-late-note">{t(lateMode === 'remainder' ? 'feesPanel.late.remainderHint' : 'feesPanel.late.catchUpHint')}</div>
                </FieldWrap>)}
            </div>}

            {/* ROW 2b — ATTRIBUTION. The same question a normal expense or other-revenue line answers,
                asked the same way: pick a BUCKET, inherit its class and scope, override if this one
                differs. The buckets are the ordinary Settings categories — there is no fee-only
                vocabulary — and the entity picker appears only when the scope names one. */}
            <div className="sfp-28">
              <FieldWrap label={bucketSide === 'Expense' ? t('feesPanel.expBucket') : t('feesPanel.revBucket')} className="sfp-w-bucket"
                title={t('feesPanel.bucketTip')}>
                <EnumSelect bare value={subtypeId} onChange={v => pickBucket(v)}>
                  {/* 47234: a charge's bucket is optional; left empty, its legs post under the Fee revenue engine bucket (db/1029) */}
                  <option value="">{bucketSide === 'Other Revenue' ? t('feesPanel.selRevBucketNone') : t('feesPanel.selBucket')}</option>
                  {bucketOptions.map(b => <option key={b.id} value={b.id}>{b.name || b.id}</option>)}
                </EnumSelect>
              </FieldWrap>

              {showCostClass && (
                <FieldWrap label={t('feesPanel.costClass')} className="sfp-w-costClass">
                  <EnumSelect bare value={costClass} onChange={v => setCostClass(v)}>
                    <option value="">{t('feesPanel.none')}</option>
                    {Object.entries(COST_CLASS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </EnumSelect>
                </FieldWrap>
              )}

              <FieldWrap label={t('feesPanel.scope')} className="sfp-w-scope">
                <EnumSelect bare value={scope} onChange={v => { setScope(v); setEntityId(''); }}>
                  <option value="">{t('feesPanel.unalloc')}</option>
                  {Object.entries(SCOPE_LABELS).filter(([v]) => v !== 'global').map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </EnumSelect>
              </FieldWrap>

              {/* The entity is required by the coherence matrix whenever a scope names one — including
                  when the bucket's own default supplied that scope and left the id blank. */}
              {['band', 'farm', 'location'].includes(scope) && (
                <FieldWrap label={SCOPE_LABELS[scope]} className="sfp-w-entity">
                  <EnumSelect bare invalid={!entityId} value={entityId} onChange={v => setEntityId(v)}>
                    <option value="">{t('feesPanel.selScope', { scope: SCOPE_LABELS[scope].toLowerCase() })}</option>
                    {entityList.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                  </EnumSelect>
                </FieldWrap>
              )}
            </div>

            {/* ROW 3 — THE CUSTOMER SIDE. Absorb has none, so the whole row is absent rather than
                greyed. What it contains FOLLOWS THE PARENT ORDER: a cash order is settled at the till
                (an account, no terms); a credit order becomes a receivable on the order's own customer,
                with the fee's own terms. */}
            {charging && (
              <div className="sfp-28">
                {/* Pass-through still shows the mirrored amount, because seeing the two sides carry the
                    SAME number is the whole point of it. Charge has one amount and no second side. */}
                {isPT && (
                  <FieldWrap label={t('feesPanel.customerPays')} className="sfp-w-custAmount"
                    hint={t('feesPanel.lockedAmount')}>
                    <AmountInput value={amount || ''} readOnly placeholder="0"
                      className="sfp-29" />
                  </FieldWrap>
                )}

                {orderIsCash ? (
                  <FieldWrap label={t('feesPanel.collectedInto')} className="sfp-w-account"
                    hintRight={t('feesPanel.cashOrderHint')}>
                    <AccountPick accounts={cashBankAccounts} value={customerAccountId} onChange={setCustomerAccountId} placeholder={t('feesPanel.cashBankPh')} btnStyle={invalid(!customerAccountId)} />
                  </FieldWrap>
                ) : (
                  <>
                    <FieldWrap label={t('feesPanel.billedTo')} className="sfp-w-account">
                      <div className="sfp-30">
                        {order?.customer_name || '—'}
                      </div>
                    </FieldWrap>
                    {/* 876 — "Customer Terms (days)" wrapped to two lines in a 150px slot and dragged
                        the row's baseline with it. Shortened rather than widened: the column beside it
                        already says the customer, so "Terms" is not ambiguous here. */}
                    <FieldWrap label={t('feesPanel.terms')} className="sfp-w-terms"
                      hintRight={t('feesPanel.creditHint')}>
                      <TextInput type="number" min="0" value={receivableDays}
                        onChange={e => setReceivableDays(e.target.value)} placeholder={t('feesPanel.eg15')} className="sfp-25" />
                    </FieldWrap>
                  </>
                )}
              </div>
            )}

            <div className="sfp-31">
              {intent === 'absorb'
                ? t('feesPanel.sumAbsorb')
                : isDeduct
                  ? deductTiming
                : isPT
                  ? t('feesPanel.sumPT')
                  : t('feesPanel.sumCharge')}
            </div>

            {/* ROW 4 — who is owed. CREDIT absorb-deduct has a real vendor payable: the vendor can be
                named, or omitted to use the automatic system Supplier Various. Its AR settlement is
                deliberately not an AR-as-expense counterpart. */}
            {intent === 'charge' ? null : isDeduct ? (
              <div className="sfp-28">
                {orderIsCash ? (
                  <FieldWrap label={t('feesPanel.cameOutOf')} className="sfp-w-account"
                    hintRight={t('feesPanel.cashDeductHint')}>
                    <AccountPick accounts={cashBankAccounts} value={accountFrom} onChange={setAccountFrom} placeholder={t('feesPanel.cashBankPh')} btnStyle={invalid(!accountFrom)} />
                  </FieldWrap>
                ) : (
                  <>
                    <FieldWrap label={t('feesPanel.vendorOpt')} className="sfp-w-vendor"
                      hintRight={t('feesPanel.variousHint')}>
                      <EnumSelect bare value={vendorId} onChange={v => setVendorId(v)}>
                        <option value="">{t('feesPanel.various')}</option>
                        {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </EnumSelect>
                    </FieldWrap>
                    <FieldWrap label={t('feesPanel.settlement')} className="sfp-w-account" hintRight={deductTiming}>
                      <div className="sfp-30">
                        {t('feesPanel.arToAp')}
                      </div>
                    </FieldWrap>
                  </>
                )}
              </div>
            ) : (
            <div className="sfp-28">
              <FieldWrap label={t('feesPanel.vendorReq')} className="sfp-w-vendor">
                <EnumSelect bare value={vendorId} onChange={val => { const v = val; setVendorId(v); if (!v) setAccountFrom(''); }}>
                  <option value="">{t('feesPanel.selVendor')}</option>
                  {vendorOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </EnumSelect>
                {vendorScope.kind === 'orphaned' && (
                  <div className="sfp-32">{vendorScope.message}</div>
                )}
              </FieldWrap>

              <FieldWrap label={t('feesPanel.payMethod')} className="sfp-seg-2">
                <div className="sfp-seg-group">
                  <button type="button" className={`sfp-seg${(payMethod === 'Cash') ? ' on' : ''}`}
                    onClick={() => { setPayMethod('Cash'); setAccountFrom(''); }}>{t('feesPanel.cash')}</button>
                  <button type="button" className={`sfp-seg${(payMethod === 'Credit') ? ' on' : ''}`}
                    onClick={() => { setPayMethod('Credit'); setAccountFrom(''); }}>{t('feesPanel.credit')}</button>
                </div>
              </FieldWrap>

              {payMethod === 'Cash' ? (
                <FieldWrap label={t('feesPanel.paidFrom')} className="sfp-w-account">
                  <AccountPick accounts={cashBankAccounts} value={accountFrom} onChange={setAccountFrom} placeholder={t('feesPanel.cashBankPh')} btnStyle={invalid(!accountFrom)} />
                </FieldWrap>
              ) : (
                <>
                  <FieldWrap label={`${t('feesPanel.vendorAcct')}${creditLocked ? ` ${t('feesPanel.auto')}` : ''}`} className="sfp-w-account">
                    <EnumSelect bare value={accountFrom} disabled={creditLocked} onChange={v => setAccountFrom(v)}>
                      <option value="">{t('feesPanel.vendorAcctOpt')}</option>
                      {(vendorId && resolvedAcct ? supplierAccounts.filter(a => a.id === resolvedAcct) : [])
                        .map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </EnumSelect>
                    {vendorId && !resolvedAcct && (
                      <div className="sfp-32">
                        {t('feesPanel.noFinAcct', { name: selVendor?.name || t('feesPanel.thisVendor') })}
                      </div>
                    )}
                  </FieldWrap>
                  <FieldWrap label={t('feesPanel.payDaysReq')} className="sfp-w-days">
                    <TextInput type="number" min="0" value={paymentDays} onChange={e => setPaymentDays(e.target.value)}
                      placeholder={t('feesPanel.eg30')} className={`sfp-inp${(!paymentDays) ? ' bad' : ''}`} />
                  </FieldWrap>
                </>
              )}
            </div>
            )}

            <div className="sfp-33">
              <span className="sfp-1">{t('feesPanel.notes')}</span>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder={t('feesPanel.notesPh')}
                className="sfp-34" />
            </div>
            <ReferenceField className="sfp-33" id="fee-reference" value={reference} onChange={setReference} />

            {/* 875 — THE FOOTER LINE, now covering EVERY intent. 874's fallback ("Choose a fee type…")
                is gone: an intent is always selected, so there is no stranded state to word for. */}
            <div className="sfp-35">{t(FOOTER_KEYS[intent])}</div>

            <FeePreview data={pv.data} error={pv.error} loading={pv.loading} />

            {credit.notice}
            {error && <div className="sfp-13">{error}</div>}

            <DrawerActions closeFirst legacyRow="act-row split mt16"
              legacyClose={<button type="button" onClick={onCancel} className="btn" disabled={submitting}>{t('feesPanel.close')}</button>}>
              {inFoot => (
                <button type="button" onClick={() => submit()} className="btn success" disabled={submitting}
                  title={submitting ? t('feesPanel.postingReason') : undefined} aria-label={submitting ? t('feesPanel.postingReason') : undefined}>
                  {submitting ? t('feesPanel.posting') : t('feesPanel.post')}
                </button>
              )}
            </DrawerActions>
          </div>
        </>
      )}
    </div>
  );
}
